import React from "react";
import { Outlet, NavLink, useNavigate, Navigate } from "react-router-dom";
import { Dropdown, Accordion } from "react-bootstrap";

import {
  FiGrid,
  FiUsers,
  FiVideo,
  FiBriefcase,
  FiCheckCircle,
  FiBell,
  FiLogOut,
  FiSettings,
  FiDatabase,
  FiChevronDown,
  FiFileText,
  FiDollarSign,
  FiTag,
  FiMessageSquare,
  FiList,
} from "react-icons/fi";

import "./AdminLayout.css";

/* =========================================
   PROTECTED ROUTE
========================================= */
export const AdminProtectedRoute = ({ children }) => {
  const token = localStorage.getItem("adminToken");

  if (!token) {
    return <Navigate to="/admin/login" replace />;
  }

  return children;
};

/* =========================================
   ADMIN LAYOUT
========================================= */
const AdminLayout = () => {
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    navigate("/admin/login");
  };

  return (
    <div className="admin-layout">
      {/* =========================================
          TOP HEADER
      ========================================= */}
      <header className="admin-topbar">
        <div className="topbar-left">
          <div className="d-flex align-items-center gap-3">
            <div className="brand-logo-box">B</div>
            <h5 className="mb-0 fw-bold">BMA Admin</h5>
          </div>
        </div>

        <div className="topbar-actions">
          {/* Notification */}
          <div className="notification-bell">
            <FiBell />
            <span className="notification-badge">9</span>
          </div>

          {/* Profile */}
          <Dropdown align="end">
            <Dropdown.Toggle as="div" className="user-profile">
              <div className="text-end d-none d-md-block">
                <div className="welcome-text">WELCOME</div>
                <div className="admin-name">ADMIN</div>
              </div>

              <div className="avatar-circle">A</div>
            </Dropdown.Toggle>

            <Dropdown.Menu
              variant="dark"
              className="shadow-lg border-0 mt-2 p-2"
              style={{ minWidth: "220px" }}
            >
              <div className="px-3 py-2 border-bottom border-secondary mb-2">
                <small className="text-muted d-block">Logged in as</small>
                <strong>BMA Admin</strong>
              </div>

              <Dropdown.Item className="rounded">
                <FiSettings className="me-2" />
                Settings
              </Dropdown.Item>

              <Dropdown.Item
                onClick={handleLogout}
                className="text-danger rounded d-flex align-items-center"
              >
                <FiLogOut className="me-2" />
                Logout
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </header>

      {/* =========================================
          BODY
      ========================================= */}
      <div className="admin-body">
        {/* =========================================
            SIDEBAR
        ========================================= */}
        <aside className="admin-sidebar">
          <div className="sidebar-scroll">
            <ul className="sidebar-nav">
              {/* DASHBOARD */}
               <li>
                <NavLink
                  to="/admin/dashboard"
                  className={({ isActive }) =>
                    `nav-item ${isActive ? "active" : ""}`
                  }
                >
                  <FiGrid />
                  <span>Dashboard</span>
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/admin/candidates"
                  className={({ isActive }) =>
                    `nav-item ${isActive ? "active" : ""}`
                  }
                >
                  <FiUsers />
                  <span>Candidates</span>
                </NavLink>
              </li>

              <li>
                <NavLink
                  to="/admin/casting-directors"
                  className={({ isActive }) =>
                    `nav-item ${isActive ? "active" : ""}`
                  }
                >
                  <FiUsers />
                  <span>Casting Directors</span>
                </NavLink>
              </li>

              <li>
                <NavLink
                  to="/admin/directors"
                  className={({ isActive }) =>
                    `nav-item ${isActive ? "active" : ""}`
                  }
                >
                  <FiVideo />
                  <span>Directors / Producers</span>
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/admin/projects"
                  className={({ isActive }) =>
                    `nav-item ${isActive ? "active" : ""}`
                  }
                >
                  <FiBriefcase />
                  <span>Projects</span>
                </NavLink>
              </li>

              <li>
                <NavLink
                  to="/admin/roles"
                  className={({ isActive }) =>
                    `nav-item ${isActive ? "active" : ""}`
                  }
                >
                  <FiCheckCircle />
                  <span>Roles</span>
                </NavLink>
              </li>

              <li>
                <NavLink
                  to="/admin/audition-list"
                  className={({ isActive }) =>
                    `nav-item ${isActive ? "active" : ""}`
                  }
                >
                  <FiList />
                  <span>Audition List</span>
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/admin/contact-list"
                  className={({ isActive }) =>
                    `nav-item ${isActive ? "active" : ""}`
                  }
                >
                  <FiMessageSquare />
                  <span>Contact List</span>
                </NavLink>
              </li>
              <li className="menu-title"><hr/></li>
              <li>
                <NavLink
                  to="/admin/notifications"
                  className={({ isActive }) =>
                    `nav-item ${isActive ? "active" : ""}`
                  }
                >
                  <FiBell />
                  <span>Notification Templates</span>
                </NavLink>
              </li>

              <li>
                <NavLink
                  to="/admin/notification-logs"
                  className={({ isActive }) =>
                    `nav-item ${isActive ? "active" : ""}`
                  }
                >
                  <FiFileText />
                  <span>Notification Logs</span>
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/admin/cost-page"
                  className={({ isActive }) =>
                    `nav-item ${isActive ? "active" : ""}`
                  }
                >
                  <FiDollarSign />
                  <span>Cost Pricing</span>
                </NavLink>
              </li>

              <li>
                <NavLink
                  to="/admin/coupon-redeem"
                  className={({ isActive }) =>
                    `nav-item ${isActive ? "active" : ""}`
                  }
                >
                  <FiTag />
                  <span>Coupons</span>
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/admin/lookups"
                  className={({ isActive }) =>
                    `nav-item ${isActive ? "active" : ""}`
                  }
                >
                  <FiMessageSquare />
                  <span>Lookup Management</span>
                </NavLink>
              </li>
               <li>
                <NavLink
                  to="/admin/payment-gateway"
                  className={({ isActive }) =>
                    `nav-item ${isActive ? "active" : ""}`
                  }
                >
                  <FiMessageSquare />
                  <span>Payment Gateway</span>
                </NavLink>
              </li>
              <li>
             
             <li>
              <NavLink
                to="/admin/payment-logs"
                className={({ isActive }) =>
                  `nav-item ${
                    isActive ? "active" : ""
                  }`
                }
              >
                <FiDollarSign />
                <span>Payment Logs</span>
              </NavLink>
            </li>
                <NavLink
                  to="/admin/testimonial"
                  className={({ isActive }) =>
                    `nav-item ${isActive ? "active" : ""}`
                  }
                >
                  <FiMessageSquare />
                  <span>Testimonials</span>
                </NavLink>
              </li>
              
            </ul>
          </div>
        </aside>

        {/* =========================================
            MAIN CONTENT
        ========================================= */}
        <main className="admin-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;